/**
 * Autopilot on the Missions page (ADR 0073): one row per project with the switch that pauses it,
 * what it does or waits for, and what it takes next. The items it skips, each with the reason,
 * open under the row; limits, kinds, the permission mode and who reviews finished work (ORGU-85)
 * open in a dialog.
 */
import { ChevronDown, Plane, Settings2 } from '@orgus/ui/icons';
import { useState } from 'react';
import { AUTOPILOT_KINDS, AUTOPILOT_MAX_MISSIONS, AUTOPILOT_SKIP_LABEL, formatUsd } from '@orgus/protocol';
import type { AutopilotPatch, AutopilotStatus, Id, ItemKind, ItemReviewMode, MachineLoadState } from '@orgus/protocol';
import { Button, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, Dialog, ErrorState, IconButton, Input, Panel, Pressable, RowStatus, Select, Switch, Tooltip, cn, plural, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { Link } from 'react-router';
import { useCan } from '../auth/useMe.ts';
import { useExt } from '../../lib/store.ts';
import { MODE_OPTIONS } from '../autonomy/model.ts';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { CPU_WAITING_EXPLANATION, formatAutopilotBarStatus, getFleetCpuWaitingStats } from '../machine-load/cpuWaiting.ts';
import { setAutopilot } from './data.ts';

const MACHINE_DEFAULT = 'machine';
const REVIEW_DEFAULT = 'default';
/** ORGU-85: who reviews finished items against their acceptance criteria. */
const REVIEW_OPTIONS = [
  { value: REVIEW_DEFAULT, label: 'Orgus when Autonomy is Full' },
  { value: 'orgus', label: 'Orgus' },
  { value: 'people', label: 'People' },
];
/** ADR 0103: Off takes no backlog item; the closed loop still runs, and Pause missions stops everything. */
const OFF_TEXT = 'Orgus takes no backlog item. It still fixes failed landings and red main. Pause missions stops everything.';
const KIND_PLURAL: Partial<Record<ItemKind, string>> = { story: 'Stories', task: 'Tasks', defect: 'Defects', spike: 'Spikes', chore: 'Chores' };

function statusOf(a: AutopilotStatus, loadSlice?: MachineLoadState): { tone: 'working' | 'neutral' | 'attention'; icon: 'working' | 'paused' | 'queued' | 'warning'; label: string } {
  if (!a.settings.on) return { tone: 'neutral', icon: 'paused', label: 'Off' };
  if (a.wait === 'paused') return { tone: 'neutral', icon: 'paused', label: 'Paused' };
  if (a.wait === 'main-red' || a.wait === 'cap' || a.wait === 'usage-limit' || a.wait === 'start-failing') return { tone: 'attention', icon: 'warning', label: 'Waiting' };
  // Its own missions against its limit, not every running mission on the page.
  if (a.running.length) {
    const label = formatAutopilotBarStatus(a.running.length, a.settings.maxMissions, loadSlice);
    return { tone: 'working', icon: 'working', label };
  }
  return { tone: 'neutral', icon: 'queued', label: 'On' };
}

export function AutopilotBar({ status, projectName, onOpenItem, defaultOpen }: { status: AutopilotStatus; projectName?: string; onOpenItem: (id: Id) => void; defaultOpen?: boolean }) {
  const canChange = useCan('operator');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [editing, setEditing] = useState(false);
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const s = statusOf(status, loadSlice);
  const cpuStats = getFleetCpuWaitingStats(loadSlice);
  const on = status.settings.on;
  // Waiting on something other than its own limit: say what, instead of what comes next.
  const held = on && !!status.wait && status.wait !== 'limit' && status.wait !== 'empty';
  // Nothing ready but items skipped: the backlog is not empty, so do not say it is.
  // Off (ADR 0103): what Off means, never the next item, which reads as if it will start.
  const text = !on ? OFF_TEXT : status.wait === 'empty' && status.skipped.length ? 'All waiting items are skipped.' : status.text;

  const toggle = async (v: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await setAutopilot(status.projectId, { on: v });
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel flush aria-label={projectName ? `Autopilot for ${projectName}` : 'Autopilot'}>
      <Collapsible defaultOpen={defaultOpen}>
        <div className="flex h-row items-center gap-3 px-4">
          <Plane className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          <span className="shrink-0 text-ui font-medium text-fg">Autopilot{projectName ? <span className="font-normal text-fg-subtle"> {projectName}</span> : null}</span>
          <RowStatus tone={s.tone} icon={s.icon} label={s.label} />
          {held || !on || !status.next ? (
            <span className="hidden min-w-0 flex-1 truncate text-caption text-fg-subtle md:block" title={text}>{text}</span>
          ) : (
            <Pressable onClick={() => onOpenItem(status.next!.itemId)} className="hidden min-w-0 flex-1 truncate text-caption text-fg-subtle hover:text-fg-muted md:block" aria-label={`Next: ${status.next.key} ${status.next.title}`}>
              Next <span className="font-mono text-fg-muted">{status.next.key}</span> {status.next.title}
            </Pressable>
          )}
          <span className="ml-auto flex shrink-0 items-center gap-1">
            {status.settings.dailyCapUsd != null && status.spentToday != null && (
              <span className="hidden text-caption text-fg-subtle num lg:inline">
                {formatUsd(status.spentToday)} of {formatUsd(status.settings.dailyCapUsd)} today
              </span>
            )}
            {status.skipped.length > 0 && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" icon={<ChevronDown />} className="hidden md:inline-flex">
                  {plural(status.skipped.length, 'skipped item', 'skipped items')}
                </Button>
              </CollapsibleTrigger>
            )}
            <Tooltip content="Autopilot settings">
              <IconButton size="sm" variant="ghost" icon={<Settings2 />} label="Autopilot settings" noTooltip disabled={!canChange} onClick={() => setEditing(true)} />
            </Tooltip>
            <Tooltip content={canChange ? (on ? 'Pause Autopilot' : 'Turn Autopilot on') : 'Operators and up can change this'}>
              <span className="flex items-center pl-2">
                <Switch checked={on} disabled={!canChange || busy} onCheckedChange={(v) => void toggle(v)} aria-label={on ? 'Pause Autopilot' : 'Turn Autopilot on'} />
              </span>
            </Tooltip>
          </span>
        </div>
        {error && (
          <div className="px-4 pb-3">
            <ErrorState variant="inline" error={error} />
          </div>
        )}
        <CollapsibleContent>
          <ul className="flex flex-col border-t border-border-subtle py-1" aria-label="Skipped items">
            {status.skipped.map((x) => (
              <li key={x.itemId}>
                <Pressable onClick={() => onOpenItem(x.itemId)} className="flex h-row items-center gap-3 px-4 hover:bg-hover">
                  <span className="w-20 shrink-0 truncate font-mono text-caption text-fg-subtle">{x.key}</span>
                  <span className="min-w-0 flex-1 truncate text-ui text-fg">{x.title}</span>
                  <span className={cn('hidden shrink-0 text-caption md:inline', x.code === 'held' ? 'text-status-attention-ink' : 'text-fg-muted')}>{AUTOPILOT_SKIP_LABEL[x.code]}</span>
                  <span className="hidden w-80 shrink-0 truncate text-caption text-fg-subtle lg:inline">{x.message}</span>
                </Pressable>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
      {editing && <AutopilotSettingsDialog status={status} projectName={projectName} onClose={() => setEditing(false)} />}
    </Panel>
  );
}

function AutopilotSettingsDialog({ status, projectName, onClose }: { status: AutopilotStatus; projectName?: string; onClose: () => void }) {
  const canBypass = useCan('admin');
  const s = status.settings;
  const [max, setMax] = useState(String(s.maxMissions));
  const [cap, setCap] = useState(s.dailyCapUsd == null ? '' : String(s.dailyCapUsd));
  const [kinds, setKinds] = useState<ItemKind[]>(s.kinds);
  const [mode, setMode] = useState(s.permissionMode ?? MACHINE_DEFAULT);
  const [review, setReview] = useState<string>(s.review ?? REVIEW_DEFAULT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const capNum = cap.trim() ? Number(cap) : null;
  const capBad = capNum != null && (!Number.isFinite(capNum) || capNum <= 0);

  const save = async () => {
    if (capBad || !kinds.length) return;
    setBusy(true);
    setError(null);
    const patch: AutopilotPatch = { maxMissions: Number(max), dailyCapUsd: capNum, kinds, permissionMode: mode === MACHINE_DEFAULT ? null : mode, review: review === REVIEW_DEFAULT ? null : (review as ItemReviewMode) };
    try {
      await setAutopilot(status.projectId, patch);
      onClose();
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={projectName ? `Autopilot for ${projectName}` : 'Autopilot'}
      description="Starts the next ready item as a mission whenever a machine has room."
      size="sm"
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void save()} disabled={capBad || !kinds.length}>
            Save settings
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select label="Missions at once" value={max} onValueChange={setMax} options={Array.from({ length: AUTOPILOT_MAX_MISSIONS }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))} />
        <Input label="Daily spend cap" hint="USD; leave empty for no cap." inputMode="decimal" placeholder="25" value={cap} onChange={(e) => setCap(e.target.value)} error={capBad ? 'Enter dollars above 0, or leave it empty.' : undefined} />
        <fieldset className="flex flex-col gap-2">
          <legend className="pb-1 text-ui font-medium text-fg">Takes</legend>
          {AUTOPILOT_KINDS.map((k) => (
            <Checkbox key={k} label={KIND_PLURAL[k]} checked={kinds.includes(k)} onCheckedChange={(v) => setKinds((ks) => (v ? [...ks, k] : ks.filter((x) => x !== k)))} />
          ))}
          {!kinds.length && <p className="text-caption text-status-danger-ink">Choose at least one kind.</p>}
        </fieldset>
        <Select
          label="Permission mode"
          value={mode}
          onValueChange={setMode}
          options={[{ value: MACHINE_DEFAULT, label: 'Machine default' }, ...MODE_OPTIONS.filter((o) => o.value !== 'default' && (o.value !== 'bypassPermissions' || canBypass || s.permissionMode === 'bypassPermissions'))]}
        />
        <Select label="Review finished work" value={review} onValueChange={setReview} options={REVIEW_OPTIONS} />
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}
